/**
 * Map places React Query hook - THREE-TIER ZOOM SYSTEM
 *
 * TIER 1 - WORLD (zoom 0-7):
 *   Server clusters with large grid, max 100 clusters
 *   No user control over clustering
 *
 * TIER 2 - REGION (zoom 8-12):
 *   Server clusters with smaller grid, max 300 clusters
 *   User can toggle clustering behavior
 *
 * TIER 3 - STREET (zoom 13+):
 *   Individual places, max 800
 *   NO clustering - every pin visible
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { getMapData } from '@/services/placesApi';
import type { MapDataResponse, MapDataParams, MapCluster, MapPlace, MapTier } from '@/types/place';
import type { Bounds } from '@/types/grid';

/**
 * Query keys for map data
 */
export const mapDataKeys = {
  all: ['mapData'] as const,
  data: (params: MapDataParams) => [...mapDataKeys.all, params] as const,
};

/**
 * Calculate bounds from places for initial auto-fit
 */
function calculateBoundsFromPlaces(places: MapPlace[]): Bounds | null {
  if (places.length === 0) return null;

  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;

  places.forEach((place) => {
    if (place.latitude > north) north = place.latitude;
    if (place.latitude < south) south = place.latitude;
    if (place.longitude > east) east = place.longitude;
    if (place.longitude < west) west = place.longitude;
  });

  const latPadding = (north - south) * 0.1 || 0.01;
  const lngPadding = (east - west) * 0.1 || 0.01;

  return {
    north: north + latPadding,
    south: south - latPadding,
    east: east + lngPadding,
    west: west - lngPadding,
  };
}

/**
 * Calculate bounds from clusters for initial auto-fit
 */
function calculateBoundsFromClusters(clusters: MapCluster[]): Bounds | null {
  if (clusters.length === 0) return null;

  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;

  clusters.forEach((cluster) => {
    if (cluster.lat > north) north = cluster.lat;
    if (cluster.lat < south) south = cluster.lat;
    if (cluster.lng > east) east = cluster.lng;
    if (cluster.lng < west) west = cluster.lng;
  });

  const latPadding = (north - south) * 0.1 || 0.5;
  const lngPadding = (east - west) * 0.1 || 0.5;

  return {
    north: north + latPadding,
    south: south - latPadding,
    east: east + lngPadding,
    west: west - lngPadding,
  };
}

interface UseMapPlacesOptions {
  enabled?: boolean;
  /** Current map viewport bounds */
  viewportBounds?: Bounds | null;
  /** Current zoom level (0-22) */
  zoom?: number;
  /** Debounce delay in ms (default: 200) */
  debounceMs?: number;
  /** Optional job filter */
  jobId?: string;
  /** Optional category filter (supports single or multiple) */
  category?: string | string[];
  /** Optional minimum rating filter */
  minRating?: number;
  /** Optional search term filter (original search query that found places, supports single or multiple) */
  searchTerm?: string | string[];
  /** Optional text search for place names */
  search?: string;
  /** Optional country filter (supports single or multiple) */
  country?: string | string[];
  /** Restrict to categories in the category_curation table (curated filter) */
  curatedCategoriesOnly?: boolean;
  /** Exclude places matching name_blacklist patterns */
  nameBlacklistActive?: boolean;
}

/**
 * Hook to fetch optimized map data with server-side clustering
 *
 * Key features:
 * - Zoom-aware: Returns clusters at low zoom, places at high zoom
 * - Viewport-aware: Only fetches data within visible bounds
 * - Debounced: Prevents excessive API calls during pan/zoom
 * - Cached: React Query caching for instant back-navigation
 */
export function useMapPlaces(options?: UseMapPlacesOptions) {
  const {
    viewportBounds,
    zoom = 10,
    debounceMs = 200,
    enabled = true,
    jobId,
    category,
    minRating,
    searchTerm,
    search,
    country,
    curatedCategoriesOnly,
    nameBlacklistActive,
  } = options || {};

  // Debounced params to prevent excessive API calls
  const [debouncedParams, setDebouncedParams] = useState<MapDataParams | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce viewport/zoom changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (viewportBounds) {
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedParams({
          north: viewportBounds.north,
          south: viewportBounds.south,
          east: viewportBounds.east,
          west: viewportBounds.west,
          zoom,  // Send exact zoom, backend determines tier
          jobId,
          category,
          minRating,
          searchTerm,
          search,
          country,
          curatedCategoriesOnly,
          nameBlacklistActive,
        });
      }, debounceMs);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [viewportBounds, zoom, debounceMs, jobId, category, minRating, searchTerm, search, country, curatedCategoriesOnly, nameBlacklistActive]);

  // Query for map data
  const query = useQuery({
    queryKey: mapDataKeys.data(debouncedParams || {
      north: 90, south: -90, east: 180, west: -180, zoom: 2,
    }),
    queryFn: async () => {
      if (!debouncedParams) {
        // Initial load - get world view clusters
        return getMapData({
          north: 90,
          south: -90,
          east: 180,
          west: -180,
          zoom: 2,
          jobId,
          category,
          minRating,
          searchTerm,
          search,
          country,
          curatedCategoriesOnly,
          nameBlacklistActive,
        });
      }
      return getMapData(debouncedParams);
    },
    staleTime: 30000, // 30 seconds
    placeholderData: (prev) => prev,
    enabled: enabled,
  });

  // Calculate initial bounds for auto-fit (ONLY ONCE on first successful load)
  const initialBoundsRef = useRef<Bounds | null>(null);
  const [initialBounds, setInitialBounds] = useState<Bounds | null>(null);

  useEffect(() => {
    // Only set initial bounds once
    if (initialBoundsRef.current !== null || !query.data) return;

    let bounds: Bounds | null = null;
    if (query.data.places && query.data.places.length > 0) {
      bounds = calculateBoundsFromPlaces(query.data.places);
    } else if (query.data.clusters && query.data.clusters.length > 0) {
      bounds = calculateBoundsFromClusters(query.data.clusters);
    }

    if (bounds) {
      initialBoundsRef.current = bounds;
      setInitialBounds(bounds);
    }
  }, [query.data]);

  return {
    // Data
    clusters: query.data?.clusters || [],
    places: query.data?.places || [],
    totalInBounds: query.data?.totalInBounds || 0,
    clustered: query.data?.clustered ?? true,
    tier: (query.data?.tier || 'world') as MapTier,

    // For initial map bounds
    initialBounds,

    // Loading states
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

export default useMapPlaces;
